import { memo, useCallback, useImperativeHandle, useState } from 'react';
import { alpha, useTheme } from '@mui/material/styles';
import useMountedRef from './useMountedRef';

/**
 * Adapts Stripe Elements to work as a controlled input inside an MUI <TextField />.
 *
 * Usage:
 *   <TextField InputProps={{ inputComponent: StripeInput }} inputProps={{ component: CardNumberElement }} />
 */
const StripeInput = ({ component: Component, inputRef, ...other }) => {
  const theme = useTheme();
  const mountedRef = useMountedRef();
  const [mountNode, setMountNode] = useState(null);

  const onReady = useCallback(
    (ref) => {
      if (mountedRef.current) setMountNode(ref);
    },
    [mountedRef]
  );

  // Expose the `focus()` method expected by MUI inputs
  useImperativeHandle(inputRef, () => ({ focus: () => mountNode?.focus?.() }), [mountNode]);

  return (
    <Component
      onReady={onReady}
      style={{
        base: {
          color: theme.palette.text.primary,
          fontSize: `${theme.typography.fontSize}px`,
          fontFamily: theme.typography.fontFamily,
          '::placeholder': {
            color: alpha(theme.palette.text.primary, 0.42),
          },
        },
        invalid: {
          color: theme.palette.text.primary,
        },
      }}
      {...other}
    />
  );
};

export default memo(StripeInput);
